/**
 * The three board columns that are facts rather than forecasts.
 *
 * **Age** decides what a rank is worth over the rest of a career and, at the
 * edges, over the rest of a season: the same consensus rank on a 23-year-old
 * and a 31-year-old are not the same bet.
 *
 * **Last year's positional finish** is the number every drafter in the room
 * half-remembers and gets wrong. Printing it next to the consensus rank turns
 * "why is he this cheap?" into a question with a visible answer — either the
 * experts are fading a finish that happened, or the room is paying for one
 * that did not.
 *
 * **Team offence rank** is the context both of those sit inside. A back on the
 * 32nd offence and a back on the 1st are not competing for the same touchdowns.
 *
 * All three come from `public/player-context.json`, built offline from
 * nflverse by `model/scripts/fetch_player_context.py`. The join is by
 * normalized name and position — the same key `playerMatch.js` and
 * `model/ffmodel/match.py` both produce — except the offence rank, which joins
 * on the team the player is on **now**. A back traded into a better offence
 * should read as being in a better offence today, not last October.
 */

import { canonicalTeam, indexPlayers } from './playerMatch.js';

const CONTEXT_JSON = '/player-context.json';

/** Top and bottom thirds of the league, for colouring the offence column. */
export const OFFENSE_STRONG = 10;
export const OFFENSE_WEAK = 23;

export const fetchPlayerContext = async () => {
  const response = await fetch(CONTEXT_JSON);
  if (!response.ok) {
    throw new Error(`Could not load player context (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('Player context was empty.');
  }
  return payload;
};

let contextCache = null;

export const getPlayerContextCached = async () => {
  if (contextCache) return contextCache;
  contextCache = await fetchPlayerContext();
  return contextCache;
};

/**
 * Attach age, last season's finish, and the team's offence rank to every row.
 *
 * Additive: a row the feed does not know about keeps everything it had and
 * renders those three columns empty. Kickers and defenses land there by
 * design — nflverse's player stat lines do not score either one, so there is
 * no honest positional finish to print.
 */
export const mergePlayerContext = (board, payload) => {
  const lookup = indexPlayers(payload?.players || []);

  const offenseByTeam = new Map();
  for (const team of payload?.teams || []) {
    offenseByTeam.set(canonicalTeam(team.team), team);
  }

  let matched = 0;
  const merged = board.map((row) => {
    const hit = lookup(row);
    // A defense's "team" is itself, and its offence rank is its own offence —
    // which is exactly backwards for a streaming decision, so it is left off.
    const offense =
      row.position === 'DST' ? null : offenseByTeam.get(canonicalTeam(row.team)) || null;
    if (!hit && !offense) return row;
    if (hit) matched += 1;

    return {
      ...row,
      age: hit?.age ?? null,
      yearsExp: hit?.yearsExp ?? null,
      lastPosRank: hit?.lastPosRank ?? null,
      lastPoints: hit?.lastPoints ?? null,
      lastGames: hit?.lastGames ?? null,
      lastTeam: hit?.lastTeam ?? null,
      teamOffenseRank: offense?.offenseRank ?? null,
      teamPointsPerGame: offense?.pointsPerGame ?? null,
    };
  });

  return { board: merged, matched, priorSeason: payload?.priorSeason ?? null };
};

/** "RB4", or null where there is no finish worth printing. */
export const formatLastFinish = (row) => {
  if (row?.lastPosRank == null) return null;
  return `${row.position}${row.lastPosRank}`;
};

/**
 * Why he finished where he did — the part a bare rank hides.
 *
 * A back who finished RB28 in seventeen games and one who finished RB28 in
 * eight are opposite bets, and the games played is the only thing on the row
 * that separates them.
 */
export const formatLastFinishTooltip = (row, priorSeason) => {
  const season = priorSeason ?? 'last season';
  if (row?.lastPosRank == null) {
    if (row?.position === 'K' || row?.position === 'DST') {
      return `No ${season} finish — kickers and defenses are not scored from this feed`;
    }
    return row?.yearsExp === 0
      ? `No ${season} finish — rookie`
      : `No ${season} finish — he did not record a snap`;
  }

  const parts = [
    `${season}: ${row.position}${row.lastPosRank}`,
    row.lastPoints != null ? `${Math.round(row.lastPoints)} half-PPR points` : null,
    row.lastGames != null ? `in ${row.lastGames} game${row.lastGames === 1 ? '' : 's'}` : null,
  ].filter(Boolean);

  const line = `${parts[0]} — ${parts.slice(1).join(' ')}`;
  if (row.lastTeam && canonicalTeam(row.lastTeam) !== canonicalTeam(row.team)) {
    return `${line}, with ${row.lastTeam}`;
  }
  return line;
};

/** The offence rank, spelled out with the number underneath it. */
export const formatOffenseTooltip = (row, priorSeason) => {
  if (row?.teamOffenseRank == null) {
    return row?.position === 'DST'
      ? 'Offence rank is about the players an offence feeds, not the defense'
      : 'No offence rank — he is not on a roster we have a team for';
  }
  const season = priorSeason ?? 'Last season';
  return (
    `${row.team} scored ${row.teamPointsPerGame} points a game in ${season} — ` +
    `${row.teamOffenseRank} of 32. Offensive scoring only: defensive and ` +
    'return touchdowns are excluded.'
  );
};
